import { useState, useRef, useEffect } from "react";

type Period = "day" | "week" | "month" | "halfyear" | "year";

const periodLabels: Record<Period, string> = {
  day: "单日",
  week: "周",
  month: "月",
  halfyear: "半年",
  year: "全年",
};

const generateData = (period: Period, userId: string) => {
  const seed = userId.charCodeAt(userId.length - 1);
  if (period === "day") {
    return Array.from({ length: 24 }, (_, i) => ({
      label: `${i}时`,
      current: Math.round((Math.sin(i / 4) * 0.5 + 1) * (seed % 5 + 2) * 10) / 10,
      prev: Math.round((Math.sin(i / 4) * 0.5 + 0.9) * (seed % 5 + 2) * 10) / 10,
    }));
  }
  if (period === "week") {
    const days = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];
    return days.map((label, i) => ({
      label,
      current: Math.round((seed % 5 + 3) * (1 + Math.sin(i) * 0.3) * 10) / 10,
      prev: Math.round((seed % 5 + 2.5) * (1 + Math.sin(i) * 0.2) * 10) / 10,
    }));
  }
  if (period === "month") {
    return Array.from({ length: 30 }, (_, i) => ({
      label: `${i + 1}日`,
      current: Math.round((seed % 4 + 2) * (1 + Math.sin(i / 5) * 0.4) * 10) / 10,
      prev: Math.round((seed % 4 + 1.8) * (1 + Math.sin(i / 5) * 0.3) * 10) / 10,
    }));
  }
  if (period === "halfyear") {
    const months = ["11月", "12月", "1月", "2月", "3月", "4月"];
    return months.map((label, i) => ({
      label,
      current: Math.round((seed % 10 + 50) * (1 + Math.sin(i) * 0.2) * 10) / 10,
      prev: Math.round((seed % 10 + 45) * (1 + Math.sin(i) * 0.15) * 10) / 10,
    }));
  }
  const months = ["5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月", "1月", "2月", "3月", "4月"];
  return months.map((label, i) => ({
    label,
    current: Math.round((seed % 10 + 50) * (1 + Math.sin(i / 2) * 0.3) * 10) / 10,
    prev: Math.round((seed % 10 + 45) * (1 + Math.sin(i / 2) * 0.2) * 10) / 10,
  }));
};

const aiReportText = (userId: string, period: Period, data: { label: string; current: number; prev: number }[]) => {
  const total = data.reduce((s, d) => s + d.current, 0).toFixed(1);
  const prevTotal = data.reduce((s, d) => s + d.prev, 0).toFixed(1);
  const diff = ((Number(total) - Number(prevTotal)) / Number(prevTotal) * 100).toFixed(1);
  const maxItem = data.reduce((a, b) => a.current > b.current ? a : b);
  const minItem = data.reduce((a, b) => a.current < b.current ? a : b);

  return `【AI用气数据深度分析报告】
用户编号：${userId} · 分析周期：${periodLabels[period]}

一、用气量概况
本${periodLabels[period]}累计用气量：${total} m³
上${periodLabels[period]}累计用气量：${prevTotal} m³
同比变化：${Number(diff) >= 0 ? "+" : ""}${diff}%（${Number(diff) >= 0 ? "增加" : "减少"}）

二、用气规律分析
▶ 用气高峰：${maxItem.label}，用气量 ${maxItem.current} m³
▶ 用气低谷：${minItem.label}，用气量 ${minItem.current} m³
▶ 日均用气：${(Number(total) / data.length).toFixed(2)} m³

三、AI异常检测
经AI模型分析，本${periodLabels[period]}用气数据整体正常，未发现明显异常波动。用气规律与历史数据吻合度达 ${Math.floor(Math.random() * 10 + 88)}%。

四、AI预测与建议
预测下${periodLabels[period]}用气量：${(Number(total) * (1 + Math.random() * 0.1 - 0.05)).toFixed(1)} m³
1. 用气量${Number(diff) >= 0 ? "增加" : "减少"}趋势明显，建议${Number(diff) >= 0 ? "关注用气安全，确保设备正常运行" : "检查是否存在设备故障或用户用气习惯变化"}
2. 建议在用气高峰时段（${maxItem.label}）加强设备压力监测
3. 账户余额充足性：按当前用气量，预计可用 ${Math.floor(Math.random() * 20 + 15)} 天`;
};

interface Props {
  userId: string;
  userName: string;
  onClose: () => void;
}

export default function MeterDeepAnalysis({ userId, userName, onClose }: Props) {
  const [period, setPeriod] = useState<Period>("month");
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState("");
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const data = generateData(period, userId);
  const maxVal = Math.max(...data.map(d => Math.max(d.current, d.prev)));

  useEffect(() => {
    drawChart();
  }, [period, data]);

  const drawChart = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const padL = 40, padR = 10, padT = 10, padB = 30;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const step = chartW / (data.length - 1);

    // Grid
    ctx.strokeStyle = "#f3f4f6";
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = padT + (chartH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(W - padR, y);
      ctx.stroke();
      ctx.fillStyle = "#9ca3af";
      ctx.font = "10px sans-serif";
      ctx.fillText((maxVal * (1 - i / 4)).toFixed(1), 2, y + 4);
    }

    // Prev line (dashed)
    ctx.strokeStyle = "#d1d5db";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    data.forEach((d, i) => {
      const x = padL + i * step;
      const y = padT + chartH - (d.prev / maxVal) * chartH;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.setLineDash([]);

    // Current line
    ctx.strokeStyle = "#E8531A";
    ctx.lineWidth = 2;
    ctx.beginPath();
    data.forEach((d, i) => {
      const x = padL + i * step;
      const y = padT + chartH - (d.current / maxVal) * chartH;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Fill
    ctx.fillStyle = "rgba(232,83,26,0.08)";
    ctx.beginPath();
    data.forEach((d, i) => {
      const x = padL + i * step;
      const y = padT + chartH - (d.current / maxVal) * chartH;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.lineTo(padL + (data.length - 1) * step, padT + chartH);
    ctx.lineTo(padL, padT + chartH);
    ctx.closePath();
    ctx.fill();

    // X labels
    ctx.fillStyle = "#9ca3af";
    ctx.font = "9px sans-serif";
    const labelStep = Math.max(1, Math.floor(data.length / 8));
    data.forEach((d, i) => {
      if (i % labelStep === 0) {
        const x = padL + i * step;
        ctx.fillText(d.label, x - 8, H - 5);
      }
    });
  };

  const generateReport = () => {
    setGenerating(true);
    setReport("");
    const text = aiReportText(userId, period, data);
    let i = 0;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (i < text.length) {
        setReport(text.slice(0, i + 1));
        i += 3;
      } else {
        setReport(text);
        if (timerRef.current) clearInterval(timerRef.current);
        setGenerating(false);
      }
    }, 15);
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const totalCurrent = data.reduce((s, d) => s + d.current, 0).toFixed(1);
  const totalPrev = data.reduce((s, d) => s + d.prev, 0).toFixed(1);
  const diff = ((Number(totalCurrent) - Number(totalPrev)) / Number(totalPrev) * 100).toFixed(1);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-[900px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900">表具深度分析 - {userName}</h3>
            <p className="text-xs text-gray-400 mt-0.5">设备编号：{userId} · AI历史数据对比分析</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500 text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Period selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">数据周期：</span>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {(Object.keys(periodLabels) as Period[]).map(p => (
                <button
                  key={p}
                  onClick={() => { setPeriod(p); setReport(""); }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${period === p ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {periodLabels[p]}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: `本${periodLabels[period]}用气量`, value: `${totalCurrent} m³`, color: "text-[#E8531A]", bg: "bg-orange-50" },
              { label: `上${periodLabels[period]}用气量`, value: `${totalPrev} m³`, color: "text-gray-700", bg: "bg-gray-50" },
              { label: "同比变化", value: `${Number(diff) >= 0 ? "+" : ""}${diff}%`, color: Number(diff) >= 0 ? "text-red-600" : "text-green-600", bg: Number(diff) >= 0 ? "bg-red-50" : "bg-green-50" },
              { label: "日均用气", value: `${(Number(totalCurrent) / data.length).toFixed(2)} m³`, color: "text-teal-600", bg: "bg-teal-50" },
            ].map((s, i) => (
              <div key={i} className={`${s.bg} rounded-xl p-4`}>
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className={`text-xl font-bold ${s.color} mt-1`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-800">用气量对比曲线</h4>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-0.5 bg-[#E8531A]"></div>
                  <span className="text-xs text-gray-500">本{periodLabels[period]}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-0.5 bg-gray-300" style={{ borderTop: "2px dashed #d1d5db" }}></div>
                  <span className="text-xs text-gray-500">上{periodLabels[period]}</span>
                </div>
              </div>
            </div>
            <canvas ref={canvasRef} width={820} height={180} className="w-full" />
          </div>

          {/* Bar comparison */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">分段用气量对比</h4>
            <div className="flex items-end gap-1 h-24">
              {data.slice(0, Math.min(data.length, 20)).map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className="w-full flex flex-col justify-end gap-0.5" style={{ height: "80px" }}>
                    <div className="w-full bg-gray-200 rounded-sm" style={{ height: `${(d.prev / maxVal) * 70}px` }} />
                    <div className="w-full bg-[#E8531A] rounded-sm" style={{ height: `${(d.current / maxVal) * 70}px` }} />
                  </div>
                  {i % Math.max(1, Math.floor(data.length / 10)) === 0 && (
                    <span className="text-[8px] text-gray-400 whitespace-nowrap">{d.label}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* AI Report */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-50">
                  <i className="ri-robot-2-line text-[#E8531A] text-sm"></i>
                </div>
                <h4 className="text-sm font-semibold text-gray-800">AI深度分析报告</h4>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={generateReport}
                  disabled={generating}
                  className="flex items-center gap-2 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-xs font-medium cursor-pointer hover:bg-[#d44a15] disabled:opacity-60 whitespace-nowrap"
                >
                  {generating ? <><i className="ri-loader-4-line animate-spin"></i>生成中</> : <><i className="ri-robot-2-line"></i>AI生成报告</>}
                </button>
                {report && (
                  <button className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                    <i className="ri-file-pdf-line text-red-500"></i>导出PDF
                  </button>
                )}
              </div>
            </div>
            {report ? (
              <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-700 leading-relaxed whitespace-pre-line font-mono">
                {report}
                {generating && <span className="animate-pulse text-[#E8531A]">|</span>}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <i className="ri-robot-2-line text-gray-300 text-3xl mb-2 block"></i>
                <p className="text-sm text-gray-400">点击「AI生成报告」，自动分析用气数据并生成深度报告</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
